/**
 * In-memory session store (client-only pivot, Phase 2).
 *
 * Holds the ONE recording currently being processed. Deliberately memory-only:
 * closing the tab discards everything (zero-storage product principle) — the
 * unload guard warns while un-exported content exists.
 *
 * Implemented as a module-level external store consumed via
 * `useSyncExternalStore` — no state library dependency.
 */
"use client";

import { useSyncExternalStore } from "react";
import type { TranscriptSegment } from "@/lib/types/domain";

/**
 * The linear flow of the app:
 * idle         — no audio selected yet (landing / dropzone)
 * transcribing — chunked transcription in progress (Phase 3)
 * working      — transcript + notes workspace (Phase 4)
 */
export type FlowState = "idle" | "transcribing" | "working";

export interface TranscriptionProgress {
  doneChunks: number;
  totalChunks: number;
}

export interface SessionState {
  flow: FlowState;
  /** The selected audio file — a handle, not bytes in memory. */
  audioFile: File | null;
  /**
   * Steering context typed on the staged screen (fed to Whisper's prompt);
   * kept so the workspace can prefill the notes focus field with it.
   */
  transcriptionContext: string;
  progress: TranscriptionProgress | null;
  segments: TranscriptSegment[];
  /** TipTap document JSON once notes exist (Phase 4). */
  notesDoc: unknown | null;
  /**
   * True while one-shot notes generation is in flight — the workspace
   * expands the collapsed notes rail to the full split for the skeleton.
   */
  notesGenerating: boolean;
  /**
   * True when the workspace holds the bundled sample interview rather
   * than a user recording — notes "generation" is mocked locally then
   * (no key, no API call; see lib/client/sample-notes.ts).
   */
  isSample: boolean;
  /**
   * True while the tab holds content the user has not exported —
   * drives the beforeunload warning and the reset confirmation.
   */
  hasUnexportedContent: boolean;
  /**
   * Fatal pipeline error message (transcription failed, bad audio, …).
   * Set while flow stays "transcribing" so the interrupted card can offer
   * try-again (the audioFile handle is kept) or discard.
   */
  error: string | null;
}

const initialState: SessionState = {
  flow: "idle",
  audioFile: null,
  transcriptionContext: "",
  progress: null,
  segments: [],
  notesDoc: null,
  notesGenerating: false,
  isSample: false,
  hasUnexportedContent: false,
  error: null,
};

let state: SessionState = initialState;
const listeners = new Set<() => void>();

/**
 * The one in-flight async pipeline (transcription or notes generation)
 * the session owns. Kept here so EVERY discard path — the workspace's
 * start-over, the header's go-home, the progress screen's cancel — kills
 * the work the same way; a controller left running after `resetSession()`
 * would keep spending the user's key and then write its result into
 * whatever session exists by the time it settles.
 */
let activeAbort: AbortController | null = null;

/** Claim the session's cancellation scope; any previous work is aborted. */
export function createSessionAbort(): AbortController {
  activeAbort?.abort();
  activeAbort = new AbortController();
  return activeAbort;
}

function setState(partial: Partial<SessionState>): void {
  state = { ...state, ...partial };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Full session state; re-renders on every store update. */
export function useSessionState(): SessionState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => initialState,
  );
}

/** Non-hook accessor for imperative code paths. */
export function getSessionState(): SessionState {
  return state;
}

// ── Actions ────────────────────────────────────────────────────────────────

/** Select the audio file and enter the transcription phase. */
export function startSession(file: File, transcriptionContext = ""): void {
  setState({
    ...initialState,
    flow: "transcribing",
    audioFile: file,
    transcriptionContext,
    hasUnexportedContent: true,
  });
}

export function setTranscriptionProgress(progress: TranscriptionProgress): void {
  setState({ progress });
}

/** Transcription finished — enter the working (transcript + notes) phase. */
export function completeTranscription(segments: TranscriptSegment[]): void {
  // Zero segments (silent audio, or a lenient endpoint returning empty
  // text) leave nothing to lose — the unload guard must not warn over an
  // empty workspace. Content created later re-arms the flag as usual.
  setState({
    flow: "working",
    progress: null,
    segments,
    hasUnexportedContent: segments.length > 0,
  });
}

export function updateSegments(segments: TranscriptSegment[]): void {
  setState({ segments, hasUnexportedContent: true });
}

export function setNotesDoc(notesDoc: unknown): void {
  setState({ notesDoc, hasUnexportedContent: true });
}

export function setNotesGenerating(notesGenerating: boolean): void {
  setState({ notesGenerating });
}

/**
 * Record a transcription failure. Stays in the "transcribing" flow with the
 * file handle intact so the interrupted card can restart the same file;
 * partial progress is kept for the stalled progress bar.
 */
export function failTranscription(message: string): void {
  // Nothing recoverable exists after a pipeline failure — drop the
  // unexported flag so the unload guard doesn't warn over lost work.
  setState({ error: message, hasUnexportedContent: false });
}

/**
 * Enter the workspace with bundled demo content (the "open a sample"
 * path — no key, no upload). Not user work, so the unload guard stays
 * disarmed until the user edits or generates something.
 */
export function loadSample(file: File, segments: TranscriptSegment[]): void {
  setState({
    ...initialState,
    flow: "working",
    audioFile: file,
    segments,
    isSample: true,
  });
}

/** The user exported — content is safe to lose until the next edit. */
export function markExported(): void {
  setState({ hasUnexportedContent: false });
}

/**
 * Discard everything and return to idle: abort whatever pipeline is in
 * flight, then clear the state. Callers own the "are you sure?"
 * confirmation when `hasUnexportedContent` is true.
 */
export function resetSession(): void {
  activeAbort?.abort();
  activeAbort = null;
  state = initialState;
  listeners.forEach((listener) => listener());
}
